import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { toast } from 'ui/sonner'

import { edgeClient } from '~/api/edge'
import { useIsOnline } from '~/hooks/use-is-online'
import { useSettingsStore } from '~/stores/settings'
import { extractTokenPayload } from '~/utils/extract-token-payload'

import { AudioRecorder } from '../audio-recorder'

import { getAudioFileExtension } from './utils'

type SpeechToTextButtonProps = {
  onTextFetched: (text: string) => void
  setIsLoading: (isLoading: boolean) => void
}

export const SpeechToTextButton = ({ setIsLoading, onTextFetched }: SpeechToTextButtonProps) => {
  const learnLanguage = useSettingsStore(state => state.learnLanguage)
  const sourceLanguage = useSettingsStore(state => state.sourceLanguage)
  const isOnline = useIsOnline()
  const { t } = useTranslation()

  const tokenPayload = extractTokenPayload()

  return (
    <div
      onClick={() => {
        if (!isOnline) toast.error(t('This feature only works with internet'))

        if (!tokenPayload?.subscriptionId) toast.error(t('This feature is for subscribers only'))
      }}
    >
      <div className={clsx(!isOnline && !tokenPayload?.subscriptionId && 'pointer-events-none')}>
        <AudioRecorder
          onAudioGenerated={blob => {
            setIsLoading(true)
            const a = getAudioFileExtension(blob.type)
            if (!learnLanguage || !sourceLanguage) return

            edgeClient.speech
              .$post({
                form: {
                  languageA: learnLanguage.value,
                  languageB: sourceLanguage.value,
                  file: new File([blob], `audio.${a}`, { type: blob.type }),
                },
              })
              .then(async res => ({ text: await res.text(), status: res.status }))
              .then(({ text, status }) => {
                if (!text.trim())
                  return toast.error(
                    status === 403
                      ? t('You have reached the subscription limit')
                      : t(`Cant process the audio Please try again later`),
                  )

                onTextFetched(text)
              })
              .catch(error => {
                toast.error(t('Network error'))
                console.error({ message: 'Network error', error })
              })
              .finally(() => setIsLoading(false))
          }}
        />
      </div>
    </div>
  )
}
